import messages from './intl/index.js';
import styles from './Pagination.module.scss.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMessageFormatter } from '../i18n/format.js';
import { Text } from '../text/Text.js';
import { Button } from '../button/Button.js';
import classNames from 'clsx';
import { MdNavigateBefore } from '../node_modules/react-icons/md/index.js';

function Pagination(props) {
  const {
    page,
    pageSize,
    totalCount,
    onPageChange,
    layout = 'nav-only',
    loadingState = 'initial',
    variant = 'default',
    className: callerClassName,
    tag
  } = props;
  const className = classNames('dsx-component', styles['pagination'], callerClassName);
  const pagesClassName = classNames(styles['pages'], styles[loadingState]);
  const {
    formatMessage
  } = useMessageFormatter(messages);
  const isLoading = loadingState === 'loading';
  const isIconOnly = layout !== '9-slot-full';
  const buttonVariant = variant === 'default' ? 'default' : 'ui-control';
  const pageCount = Math.ceil(totalCount / pageSize);
  const start = Math.min(totalCount, page * pageSize + 1);
  const end = Math.min(totalCount, page * pageSize + pageSize);
  const summaryEl = /*#__PURE__*/jsx(Text, {
    children: formatMessage('startToEndOutOfTotalResults', {
      start,
      end,
      totalCount
    })
  });
  const pagesEls = layout === 'nav-only' ? /*#__PURE__*/jsx(Text, {
    className: styles['nav-only-count'],
    children: formatMessage('pageOfPageCount', {
      page: page + 1,
      pageCount
    })
  }) : /*#__PURE__*/jsx("div", {
    className: pagesClassName,
    children: pageRange(page, pageCount, layout).map((p, i) => p === '...' ? /*#__PURE__*/jsx(Button, {
      variant: buttonVariant,
      size: "small",
      disabled: true,
      children: "..."
    }, `ellipsis-${i}`) : p === page ? /*#__PURE__*/jsx(Button, {
      variant: "primary",
      size: "small",
      disabled: isLoading,
      className: styles['selected'],
      "aria-current": "page",
      [`data-dsx--pagination--page-${p}`]: tag,
      children: (p + 1).toLocaleString()
    }, p) : /*#__PURE__*/jsx(Button, {
      variant: buttonVariant,
      size: "small",
      disabled: isLoading,
      onClick: e => {
        e.preventDefault();
        onPageChange(p);
      },
      [`data-dsx--pagination--page-${p}`]: tag,
      children: (p + 1).toLocaleString()
    }, p))
  });
  return /*#__PURE__*/jsxs("nav", {
    className: className,
    "aria-label": formatMessage('pagination'),
    "data-dsx--pagination": tag,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles['navigation'],
      children: [/*#__PURE__*/jsx(Button, {
        icon: MdNavigateBefore,
        iconOnly: isIconOnly,
        variant: buttonVariant,
        size: "small",
        disabled: isLoading || page === 0,
        onClick: e => {
          e.preventDefault();
          if (page > 0) {
            onPageChange(page - 1);
          }
        },
        "data-dsx--pagination--previous": tag,
        children: formatMessage('previous')
      }), pagesEls, /*#__PURE__*/jsx(Button, {
        iconOnly: isIconOnly,
        actionIndicator: "MdNavigateNext",
        variant: buttonVariant,
        size: "small",
        disabled: isLoading || page === pageCount - 1,
        onClick: e => {
          e.preventDefault();
          if (page < pageCount - 1) {
            onPageChange(page + 1);
          }
        },
        "data-dsx--pagination--next": tag,
        children: formatMessage('next')
      })]
    }), summaryEl]
  });
}
function pageRange(page, pageCount, layout) {
  if (layout === 'nav-only') {
    return [];
  }
  const lastPage = pageCount - 1;
  if (layout === '7-slot') {
    if (pageCount <= 7) {
      return range(0, lastPage);
    }
    if (page <= 1 || page >= lastPage - 1) {
      return [...range(0, 2), '...', ...range(lastPage - 2, lastPage)];
    }
    return [0, '...', ...range(page - 1, page + 1), '...', lastPage];
  }
  if (pageCount <= 9) {
    return range(0, lastPage);
  }
  if (page <= 1 || page >= pageCount - 2) {
    return [...range(0, 3), '...', ...range(pageCount - 4, lastPage)];
  }
  if (page <= 4) {
    return [...range(0, page + 2), '...', ...range(pageCount + page - 5, lastPage)];
  }
  if (page >= pageCount - 5) {
    return [...range(0, page - pageCount + 5), '...', ...range(page - 2, lastPage)];
  }
  return [0, '...', ...range(page - 2, page + 2), '...', lastPage];
}
function range(from, to) {
  return Array(to - from + 1).fill(undefined).map((_, n) => n + from);
}

export { Pagination };
